import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Search, Plus, Truck, PieChart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WholesaleStatusBadge, StockBadge } from "@/components/wholesale/status-badge";
import { BulkImportDialog } from "@/components/shipping/bulk-import-dialog";
import { usePartners, useWholesaleOrders } from "@/hooks/use-wholesale";
import { useDebounce } from "@/hooks/use-debounce";
import { WHOLESALE_STATUS_LABELS, WHOLESALE_STORE_ID, type WholesaleStatus } from "@/lib/wholesale";
import { fmtKm, fmtUnixDate } from "@/lib/wholesale-format";

const ALL = "__all__";

/** Kuriru se šalje ono što je potvrđeno ili spakovano; nacrt nema rezervisanu
 *  zalihu, a poslano/završeno/otkazano više nema šta tražiti kod kurira. */
const IMPORTABLE_STATUSES: WholesaleStatus[] = ["confirmed", "packed"];

export function WholesaleOrdersPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(ALL);
  const [partnerId, setPartnerId] = useState(ALL);
  const [search, setSearch] = useState("");
  const debounced = useDebounce(search);
  const partners = usePartners();
  const { data, isLoading, error } = useWholesaleOrders({
    status: status === ALL ? "" : status,
    partnerId: partnerId === ALL ? "" : partnerId,
    search: debounced,
  });
  const orders = data ?? [];
  const partnerList = partners.data ?? [];

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [importOpen, setImportOpen] = useState(false);
  const importable = useMemo(() => orders.filter((o) => IMPORTABLE_STATUSES.includes(o.status)), [orders]);
  // Filteri mijenjaju skup redova pod selekcijom — nositi staru selekciju dalje
  // znači slati kuriru narudžbe koje korisnik više ne vidi.
  useEffect(() => { setSelected(new Set()); }, [status, partnerId, debounced]);

  const selectedLocalIds = orders.filter((o) => selected.has(o.id)).map((o) => String(o.localId));
  const allImportableSelected = importable.length > 0 && importable.every((o) => selected.has(o.id));

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected(allImportableSelected ? new Set() : new Set(importable.map((o) => o.id)));
  }

  return (
    <div className="p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-sm font-medium">Narudžbe veleprodaje</h2>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setImportOpen(true)}>
              <Truck className="h-3.5 w-3.5" />
              Uvezi u brzu poštu ({selected.size})
            </Button>
          )}
          <Button size="sm" variant="outline" className="gap-1.5" asChild>
            <Link to="/wholesale/analytics">
              <PieChart className="h-3.5 w-3.5" />
              Analitika
            </Link>
          </Button>
          <Button size="sm" className="gap-1.5" asChild>
            <Link to="/wholesale/orders/new">
              <Plus className="h-3.5 w-3.5" />
              Nova narudžba
            </Link>
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            className="h-8 text-xs pl-8"
            placeholder="Pretraži po broju ili partneru…"
            aria-label="Pretraži narudžbe"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" aria-label="Status">
            <SelectValue placeholder="Svi statusi" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Svi statusi</SelectItem>
            {(Object.keys(WHOLESALE_STATUS_LABELS) as WholesaleStatus[]).map((s) => (
              <SelectItem key={s} value={s}>{WHOLESALE_STATUS_LABELS[s]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={partnerId} onValueChange={setPartnerId}>
          <SelectTrigger className="w-48" aria-label="Partner">
            <SelectValue placeholder="Svi partneri" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Svi partneri</SelectItem>
            {partnerList.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      ) : error ? (
        <div className="ring-1 ring-destructive/30 p-6 text-center text-xs text-destructive">
          Narudžbe nisu učitane: {(error as Error).message}
        </div>
      ) : (
        <div className="ring-1 ring-foreground/10 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/50 text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                <th className="py-2 px-2 w-8">
                  <Checkbox
                    aria-label="Označi sve za uvoz"
                    checked={allImportableSelected}
                    disabled={importable.length === 0}
                    onCheckedChange={toggleAll}
                  />
                </th>
                <th className="py-2 px-2 text-left font-medium">Broj</th>
                <th className="py-2 px-2 text-left font-medium">Partner</th>
                <th className="py-2 px-2 text-left font-medium hidden sm:table-cell">Datum</th>
                <th className="py-2 px-2 text-right font-medium">Iznos</th>
                <th className="py-2 px-2 text-right font-medium">Plaćeno</th>
                <th className="py-2 px-2 text-left font-medium">Status</th>
                <th className="py-2 px-2 text-left font-medium hidden md:table-cell">Zaliha</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr
                  key={o.id}
                  onClick={() => navigate({ to: "/wholesale/orders/$orderId", params: { orderId: o.id } })}
                  className="border-b last:border-b-0 hover:bg-muted/50 transition-colors cursor-pointer"
                >
                  <td className="py-2.5 px-2" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      aria-label={`Označi ${o.number}`}
                      checked={selected.has(o.id)}
                      disabled={!IMPORTABLE_STATUSES.includes(o.status)}
                      onCheckedChange={() => toggle(o.id)}
                    />
                  </td>
                  <td className="py-2.5 px-2 font-medium tabular-nums">
                    <Link
                      to="/wholesale/orders/$orderId"
                      params={{ orderId: o.id }}
                      className="hover:underline underline-offset-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {o.number}
                    </Link>
                  </td>
                  <td className="py-2.5 px-2">{o.partnerName || "—"}</td>
                  <td className="py-2.5 px-2 text-muted-foreground tabular-nums hidden sm:table-cell">
                    {fmtUnixDate(o.createdAt)}
                  </td>
                  <td className="py-2.5 px-2 text-right tabular-nums">{fmtKm(o.total)}</td>
                  <td className="py-2.5 px-2 text-right tabular-nums text-muted-foreground">{fmtKm(o.paid)}</td>
                  <td className="py-2.5 px-2"><WholesaleStatusBadge status={o.status} /></td>
                  <td className="py-2.5 px-2 hidden md:table-cell"><StockBadge state={o.stockState} /></td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-6 text-center text-muted-foreground">Nema narudžbi</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <BulkImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        selectedOrderIds={selectedLocalIds}
        storeId={WHOLESALE_STORE_ID}
        credentialSource="all"
        invalidateKeys={[["wholesale"]]}
        onSuccess={() => setSelected(new Set())}
      />
    </div>
  );
}
